<template>
  <div class="manager-report">
    <el-card class="toolbar-card">
      <div class="toolbar">
        <div class="toolbar-title">数据看板</div>
        <div class="toolbar-actions">
          <div class="date-filter-group">
            <div class="date-buttons-row">
              <el-button 
                :type="quickRange === 'today' ? 'primary' : 'default'"
                size="small"
                @click="setQuickRange('today')"
              >
                今天
              </el-button>
              <el-button 
                :type="quickRange === 'week' ? 'primary' : 'default'"
                size="small"
                @click="setQuickRange('week')"
              >
                本周
              </el-button>
              <el-button 
                :type="quickRange === 'month' ? 'primary' : 'default'"
                size="small"
                @click="setQuickRange('month')"
              >
                本月
              </el-button>
            </div>
            <div class="date-buttons-row">
              <el-button 
                :type="quickRange === 'quarter' ? 'primary' : 'default'"
                size="small"
                @click="setQuickRange('quarter')"
              >
                近三个月
              </el-button>
              <el-button 
                :type="quickRange === 'halfYear' ? 'primary' : 'default'"
                size="small"
                @click="setQuickRange('halfYear')"
              >
                近半年
              </el-button>
              <el-button 
                :type="quickRange === 'year' ? 'primary' : 'default'"
                size="small"
                @click="setQuickRange('year')"
              >
                近一年
              </el-button>
            </div>
          </div>
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            :disabled-date="disableFuture"
            style="width: 320px"
            @change="handleDateChange"
          />
          <el-select
            v-model="salesmanIds"
            multiple
            filterable
            clearable
            collapse-tags
            collapse-tags-tooltip
            placeholder="全部业务员"
            style="width: 300px"
            @change="loadDashboard"
          >
            <el-option v-for="s in salesmanOptions" :key="s.id" :label="s.name" :value="s.id" />
          </el-select>
          <el-button type="primary" @click="loadDashboard">刷新</el-button>
          <el-button type="success" @click="handleExport" :loading="exportLoading">导出</el-button>
        </div>
      </div>
    </el-card>

    <div v-loading="loading">
      <!-- KPI 卡片 -->
      <el-row :gutter="16" class="kpi-row">
        <el-col :span="6" v-for="kpi in kpiList" :key="kpi.key">
          <el-card class="kpi-card" shadow="hover">
            <div class="kpi-label">{{ kpi.label }}</div>
            <div class="kpi-value">{{ formatKpiValue(kpi) }}</div>
          </el-card>
        </el-col>
      </el-row>

      <el-row :gutter="16">
        <!-- 趋势图 -->
        <el-col :span="16">
          <el-card class="chart-card" shadow="hover">
            <template #header>
              <div class="card-header">
                <span>趋势</span>
                <el-segmented v-model="chartType" :options="chartOptions" />
              </div>
            </template>
            <v-chart 
              v-if="chartOption" 
              ref="chartRef"
              :option="chartOption" 
              style="height: 360px" 
              autoresize
              class="manager-report-chart"
            />
            <el-empty v-else description="暂无数据" />
          </el-card>
        </el-col>

        <!-- 排行榜 -->
        <el-col :span="8">
          <el-card class="ranking-card" shadow="hover">
            <template #header>
              <div class="card-header">
                <span>业务员排名（按成交金额）</span>
              </div>
            </template>
            <div v-if="ranking && ranking.length" class="ranking-list">
              <div v-for="(item, index) in ranking" :key="item.salesmanId" class="ranking-item">
                <div class="rank-index">{{ index + 1 }}</div>
                <div class="rank-info">
                  <div class="rank-name">{{ item.name || '-' }}</div>
                  <div class="rank-meta">工号：{{ item.employeeNo || '-' }}</div>
                </div>
                <div class="rank-metrics">
                  <div>新增：{{ item.newCustomer }}</div>
                  <div>跟进：{{ item.follow }}</div>
                  <div>合同：{{ item.contractCreated }}</div>
                  <div class="deal">成交：{{ formatNumber(item.dealCount) }} / {{ formatCurrency(item.dealAmount) }}</div>
                </div>
              </div>
            </div>
            <el-empty v-else description="暂无数据" />
          </el-card>
        </el-col>
      </el-row>

      <!-- 意向等级分布 -->
      <el-card class="intention-card" shadow="hover">
        <template #header>
          <span>客户意向等级分布</span>
        </template>
        <div v-if="intentionDistribution && intentionDistribution.length" class="intention-list">
          <div v-for="item in intentionDistribution" :key="item.level" class="intention-item">
            <div class="intention-label">{{ item.level }}</div>
            <el-progress :percentage="item.percentage" :stroke-width="14" />
            <div class="intention-count">{{ item.count }} 人</div>
          </div>
        </div>
        <el-empty v-else description="暂无数据" />
      </el-card>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { getManagerDashboard } from '../api/managerReport'
import { getSalesmanList } from '../api/managerUser'
import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import * as echarts from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { BarChart, LineChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components'
import * as XLSX from 'xlsx'

use([CanvasRenderer, BarChart, LineChart, GridComponent, TooltipComponent, LegendComponent])

const loading = ref(false)
const exportLoading = ref(false)
const quickRange = ref('week')
const dateRange = ref([])
const salesmanIds = ref([])
const salesmanOptions = ref([])
const chartType = ref('bar')
const chartOptions = [
  { label: '柱状图', value: 'bar' },
  { label: '折线图', value: 'line' }
]
const chartRef = ref(null)

const kpis = reactive({
  newCustomer: 0,
  follow: 0,
  contractCreated: 0,
  dealCount: 0,
  dealAmount: 0
})
const trend = ref(null)
const ranking = ref([])
const intentionDistribution = ref([])

const kpiList = computed(() => [
  { key: 'newCustomer', label: '新增客户', value: kpis.newCustomer },
  { key: 'follow', label: '跟进客户', value: kpis.follow },
  { key: 'contractCreated', label: '合同创建', value: kpis.contractCreated },
  { key: 'deal', label: '成交数量/成交金额', value: { count: kpis.dealCount, amount: kpis.dealAmount } }
])

const disableFuture = (time) => time.getTime() > Date.now()

const setDefaultRange = () => {
  const now = new Date()
  let start = null
  
  if (quickRange.value === 'today') {
    start = new Date(now)
    start.setHours(0, 0, 0, 0)
    const end = new Date(now)
    end.setHours(23, 59, 59, 999)
    dateRange.value = [start, end]
  } else if (quickRange.value === 'week') {
    const day = now.getDay() || 7
    start = new Date(now)
    start.setDate(now.getDate() - day + 1)
    start.setHours(0, 0, 0, 0)
    const end = new Date(now)
    end.setHours(23, 59, 59, 999)
    dateRange.value = [start, end]
  } else if (quickRange.value === 'month') {
    start = new Date(now.getFullYear(), now.getMonth(), 1)
    start.setHours(0, 0, 0, 0)
    const end = new Date(now)
    end.setHours(23, 59, 59, 999)
    dateRange.value = [start, end]
  } else if (quickRange.value === 'quarter') {
    start = new Date(now)
    start.setMonth(now.getMonth() - 3)
    start.setHours(0, 0, 0, 0)
    const end = new Date(now)
    end.setHours(23, 59, 59, 999)
    dateRange.value = [start, end]
  } else if (quickRange.value === 'halfYear') {
    start = new Date(now)
    start.setMonth(now.getMonth() - 6)
    start.setHours(0, 0, 0, 0)
    const end = new Date(now)
    end.setHours(23, 59, 59, 999)
    dateRange.value = [start, end]
  } else if (quickRange.value === 'year') {
    start = new Date(now)
    start.setFullYear(now.getFullYear() - 1)
    start.setHours(0, 0, 0, 0)
    const end = new Date(now)
    end.setHours(23, 59, 59, 999)
    dateRange.value = [start, end]
  }
}

const setQuickRange = (range) => {
  quickRange.value = range
  setDefaultRange()
  loadDashboard()
}

const handleDateChange = () => {
  quickRange.value = ''
  loadDashboard()
}

const loadSalesmanOptions = async () => {
  try {
    const res = await getSalesmanList()
    if (res.code === '000000') {
      salesmanOptions.value = (res.data || []).map((item) => ({
        id: item.id,
        name: item.name || '业务员'
      }))
    }
  } catch (error) {
    console.error('加载业务员列表失败:', error)
  }
}

const buildParams = () => {
  const [start, end] = dateRange.value || []
  const fmt = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  const params = {
    startDate: start ? fmt(start) : undefined,
    endDate: end ? fmt(end) : undefined
  }
  // 如果有选择的业务员，传递多个ID（使用逗号分隔的字符串，后端会解析）
  if (salesmanIds.value && salesmanIds.value.length > 0) {
    params.salesmanIds = salesmanIds.value.join(',')
  }
  return params
}

const loadDashboard = async () => {
  loading.value = true
  try {
    const res = await getManagerDashboard(buildParams())
    if (res.code === '000000' && res.data) {
      Object.assign(kpis, res.data.kpis || {})
      trend.value = res.data.trend || null
      ranking.value = res.data.ranking || []
      intentionDistribution.value = res.data.intentionDistribution || []
    }
  } catch (error) {
    console.error('加载数据看板失败:', error)
    ElMessage.error(error.response?.data?.message || '加载数据看板失败')
  } finally {
    loading.value = false
  }
}

const chartOption = computed(() => {
  // 若有业务员维度数据，优先按业务员展示
  if (ranking.value && ranking.value.length) {
    const xAxis = ranking.value.map((r) => r.name || r.employeeNo || '业务员')
    const series = [
      { name: '新增客户', data: ranking.value.map((r) => r.newCustomer || 0) },
      { name: '跟进客户', data: ranking.value.map((r) => r.follow || 0) },
      { name: '合同创建', data: ranking.value.map((r) => r.contractCreated || 0) },
      { name: '成交数量', data: ranking.value.map((r) => r.dealCount || 0) },
      { name: '成交金额', data: ranking.value.map((r) => Number(r.dealAmount || 0)) }
    ]
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' }
      },
      legend: { data: series.map((s) => s.name) },
      grid: { left: 30, right: 20, top: 40, bottom: 60 },
      xAxis: { type: 'category', data: xAxis },
      yAxis: { type: 'value' },
      series: series.map((s) => ({
        ...s,
        type: chartType.value,
        label: { show: false }
      }))
    }
  }

  // 兜底：按时间趋势
  if (!trend.value || !trend.value.series || !trend.value.series.length) return null
  return {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    legend: { data: trend.value.series.map((s) => s.name) },
    grid: { left: 30, right: 20, top: 40, bottom: 40 },
    xAxis: { type: 'category', data: trend.value.xAxis || [] },
    yAxis: { type: 'value' },
    series: trend.value.series.map((s) => ({
      name: s.name,
      type: chartType.value,
      data: s.data,
      label: { show: false }
    }))
  }
})

const formatNumber = (val) => {
  if (val === undefined || val === null) return 0
  return Number(val).toLocaleString()
}

const formatCurrency = (val) => {
  const num = Number(val || 0)
  if (Number.isNaN(num)) return '0.00'
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const formatKpiValue = (kpi) => {
  if (kpi.key === 'deal' && kpi.value) {
    return `${formatNumber(kpi.value.count)} / ${formatCurrency(kpi.value.amount)}`
  }
  return formatNumber(kpi.value)
}

// 将图表转换为图片（Base64）
const getChartImage = async () => {
  if (!chartOption.value) {
    console.warn('图表配置为空')
    return null
  }

  try {
    // 等待 DOM 更新和图表完全渲染
    await nextTick()
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    let chartInstance = null
    
    // 方法1: 尝试通过组件引用获取实例
    if (chartRef.value) {
      const chartComponent = chartRef.value
      
      // vue-echarts 8.x 可能的方法
      if (typeof chartComponent.getEchartsInstance === 'function') {
        try {
          chartInstance = chartComponent.getEchartsInstance()
          if (chartInstance && typeof chartInstance.getDataURL === 'function') {
            console.log('✓ 通过 getEchartsInstance() 获取实例成功')
          } else {
            chartInstance = null
          }
        } catch (e) {
          console.warn('getEchartsInstance() 调用失败:', e)
        }
      }
      
      // 尝试访问可能的实例属性
      if (!chartInstance) {
        const possibleProps = ['echartsInstance', 'chart', 'instance', 'echarts']
        for (const prop of possibleProps) {
          if (chartComponent[prop] && typeof chartComponent[prop].getDataURL === 'function') {
            chartInstance = chartComponent[prop]
            console.log(`✓ 通过 ${prop} 属性获取实例成功`)
            break
          }
        }
      }
    }
    
    // 方法2: 通过 DOM 元素查找图表实例（最可靠的方法）
    if (!chartInstance) {
      // 首先尝试通过 chartRef 的 DOM 查找
      if (chartRef.value) {
        try {
          // vue-echarts 组件的 DOM 结构
          const componentEl = chartRef.value.$el || chartRef.value.el || chartRef.value
          if (componentEl && componentEl.querySelector) {
            const canvas = componentEl.querySelector('canvas')
            if (canvas) {
              const instance = echarts.getInstanceByDom(canvas)
              if (instance && typeof instance.getDataURL === 'function') {
                chartInstance = instance
                console.log('✓ 通过组件 DOM 查找获取实例成功')
              }
            }
          }
        } catch (e) {
          console.warn('通过组件 DOM 查找失败:', e)
        }
      }
      
      // 如果还是找不到，通过类名查找图表容器
      if (!chartInstance) {
        try {
          const chartContainer = document.querySelector('.manager-report-chart')
          if (chartContainer) {
            const canvas = chartContainer.querySelector('canvas')
            if (canvas) {
              const instance = echarts.getInstanceByDom(canvas)
              if (instance && typeof instance.getDataURL === 'function') {
                chartInstance = instance
                console.log('✓ 通过类名查找获取图表实例成功')
              }
            }
          }
        } catch (e) {
          console.warn('通过类名查找失败:', e)
        }
      }
      
      // 最后的方法：查找所有 canvas 元素
      if (!chartInstance) {
        const allCanvases = document.querySelectorAll('canvas')
        console.log(`找到 ${allCanvases.length} 个 canvas 元素`)
        
        for (const canvas of allCanvases) {
          try {
            const instance = echarts.getInstanceByDom(canvas)
            if (instance && typeof instance.getDataURL === 'function') {
              // 验证这个实例是否有数据
              try {
                const option = instance.getOption ? instance.getOption() : null
                // 如果图表有数据（有 series），或者没有 getOption 方法（旧版本），都使用
                if (!option || (option.series && option.series.length > 0)) {
                  chartInstance = instance
                  console.log('✓ 通过全局 DOM 查找获取图表实例成功')
                  break
                }
              } catch (e) {
                // 如果验证失败，也尝试使用（可能是版本问题）
                chartInstance = instance
                console.log('✓ 通过全局 DOM 查找获取图表实例成功（跳过验证）')
                break
              }
            }
          } catch (e) {
            // 继续查找下一个
            continue
          }
        }
      }
    }
    
    // 方法3: 如果 chartRef 有 $el，直接查找其下的 canvas
    if (!chartInstance && chartRef.value) {
      try {
        const component = chartRef.value
        let domElement = null
        
        // 尝试获取 DOM 元素
        if (component.$el) {
          domElement = component.$el
        } else if (component.el) {
          domElement = component.el
        } else if (component.$) {
          // Vue 3 的 $ 属性
          domElement = component.$
        }
        
        if (domElement) {
          const canvas = domElement.querySelector ? domElement.querySelector('canvas') : null
          if (canvas) {
            const instance = echarts.getInstanceByDom(canvas)
            if (instance && typeof instance.getDataURL === 'function') {
              chartInstance = instance
              console.log('✓ 通过组件 DOM 查找获取实例成功')
            }
          }
        }
      } catch (e) {
        console.warn('通过组件 DOM 查找失败:', e)
      }
    }
    
    if (!chartInstance) {
      console.error('❌ 无法获取图表实例')
      console.error('chartRef.value 类型:', typeof chartRef.value)
      console.error('chartRef.value:', chartRef.value)
      if (chartRef.value) {
        console.error('chartRef.value 的属性:', Object.keys(chartRef.value))
      }
      console.error('页面中 canvas 数量:', document.querySelectorAll('canvas').length)
      ElMessage.warning('无法获取图表实例，仅导出数据')
      return null
    }

    console.log('✓ 图表实例获取成功，开始导出图片...')

    // 导出为图片
    const imageDataUrl = chartInstance.getDataURL({
      type: 'png',
      pixelRatio: 2,
      backgroundColor: '#fff'
    })

    if (!imageDataUrl || typeof imageDataUrl !== 'string' || !imageDataUrl.startsWith('data:image')) {
      console.error('❌ 图表图片导出失败，imageDataUrl 无效')
      console.error('imageDataUrl 类型:', typeof imageDataUrl)
      console.error('imageDataUrl 前50字符:', imageDataUrl?.substring(0, 50))
      ElMessage.warning('图表图片导出失败，仅导出数据')
      return null
    }

    console.log('✓ 图表图片导出成功，数据长度:', imageDataUrl.length)

    // 提取 Base64 数据（去掉 data URI 前缀）
    const base64Data = imageDataUrl.split(',')[1] || imageDataUrl.replace(/^data:image\/\w+;base64,/, '')
    return base64Data
  } catch (error) {
    console.error('❌ 获取图表图片失败:', error)
    console.error('错误堆栈:', error.stack)
    ElMessage.error('图表导出出错: ' + error.message)
    return null
  }
}

// 导出Excel报表（包含图表）
const handleExport = async () => {
  if (exportLoading.value) {
    return
  }

  try {
    exportLoading.value = true

    // 尝试动态加载 ExcelJS
    let ExcelJS
    try {
      ExcelJS = (await import('exceljs')).default
    } catch (error) {
      console.warn('ExcelJS 未安装，使用普通导出模式（不含图表）')
      ElMessage.warning('图表导出功能需要安装 ExcelJS 库，当前使用普通导出模式。请运行: npm install exceljs')
      // 降级到普通导出（不含图表）
      await handleExportWithoutChart()
      return
    }

    // 创建工作簿
    const workbook = new ExcelJS.Workbook()

    // 1. KPI数据工作表
    const kpiSheet = workbook.addWorksheet('核心指标')
    kpiSheet.columns = [
      { header: '指标', key: 'label', width: 15 },
      { header: '数值', key: 'value', width: 15 }
    ]
    kpiSheet.addRow({ label: '新增客户', value: kpis.newCustomer || 0 })
    kpiSheet.addRow({ label: '跟进客户', value: kpis.follow || 0 })
    kpiSheet.addRow({ label: '合同创建', value: kpis.contractCreated || 0 })
    kpiSheet.addRow({ label: '成交数量/成交金额', value: `${kpis.dealCount || 0} / ${formatCurrency(kpis.dealAmount)}` })

    // 设置标题行样式
    kpiSheet.getRow(1).font = { bold: true }
    kpiSheet.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' }
    }

    // 2. 趋势图工作表（包含图表图片）
    if (chartOption.value) {
      console.log('开始获取图表图片并插入到 Excel...')
      const chartImageBase64 = await getChartImage()
      
      if (chartImageBase64) {
        console.log('图表图片获取成功，开始插入到 Excel...')
        const chartSheet = workbook.addWorksheet('趋势图表')
        
        // 添加图表标题
        chartSheet.getCell('A1').value = '趋势图表'
        chartSheet.getCell('A1').font = { size: 16, bold: true }
        chartSheet.getRow(1).height = 30
        
        try {
          // 插入图表图片
          const imageId = workbook.addImage({
            base64: chartImageBase64,
            extension: 'png'
          })
          
          console.log('图片已添加到工作簿，ID:', imageId)
          
          // 将图片插入到 A3 单元格位置
          chartSheet.addImage(imageId, {
            tl: { col: 0, row: 2 },
            ext: { width: 800, height: 450 }
          })
          
          console.log('图片已插入到工作表')
          
          // 调整行高和列宽以容纳图片
          chartSheet.getColumn(1).width = 100
          chartSheet.getRow(3).height = 450
        } catch (imageError) {
          console.error('插入图片到 Excel 失败:', imageError)
          ElMessage.warning('图表图片插入失败，但数据已成功导出')
        }
      } else {
        console.warn('图表图片获取失败，跳过图表导出')
        ElMessage.warning('图表图片获取失败，仅导出数据')
      }
    } else {
      console.log('没有图表配置，跳过图表导出')
    }

    // 3. 趋势图数据工作表（如果有数据）
    if (trend.value && trend.value.xAxis && trend.value.xAxis.length > 0) {
      const trendSheet = workbook.addWorksheet('趋势数据')
      const trendHeaders = ['日期/时间', ...(trend.value.series || []).map(s => s.name || '')]
      
      trendSheet.columns = trendHeaders.map((header, index) => ({
        header,
        key: `col${index}`,
        width: index === 0 ? 15 : 12
      }))
      
      // 添加数据
      trend.value.xAxis.forEach((xValue, index) => {
        const row = { col0: xValue }
        trend.value.series.forEach((series, seriesIndex) => {
          row[`col${seriesIndex + 1}`] = series.data && series.data[index] ? series.data[index] : 0
        })
        trendSheet.addRow(row)
      })
      
      // 设置标题行样式
      trendSheet.getRow(1).font = { bold: true }
      trendSheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' }
      }
    }

    // 4. 业务员排名数据工作表
    if (ranking.value && ranking.value.length > 0) {
      const rankingSheet = workbook.addWorksheet('业务员排名')
      rankingSheet.columns = [
        { header: '排名', key: 'rank', width: 8 },
        { header: '姓名', key: 'name', width: 12 },
        { header: '工号', key: 'employeeNo', width: 15 },
        { header: '新增客户', key: 'newCustomer', width: 12 },
        { header: '跟进客户', key: 'follow', width: 12 },
        { header: '合同创建', key: 'contractCreated', width: 12 },
        { header: '成交数量', key: 'dealCount', width: 12 },
        { header: '成交金额', key: 'dealAmount', width: 14 }
      ]
      
      ranking.value.forEach((item, index) => {
        rankingSheet.addRow({
          rank: index + 1,
          name: item.name || '-',
          employeeNo: item.employeeNo || '-',
          newCustomer: item.newCustomer || 0,
          follow: item.follow || 0,
          contractCreated: item.contractCreated || 0,
          dealCount: item.dealCount || 0,
          dealAmount: Number(item.dealAmount || 0)
        })
      })
      
      // 设置标题行样式
      rankingSheet.getRow(1).font = { bold: true }
      rankingSheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' }
      }
    }

    // 5. 意向等级分布数据工作表
    if (intentionDistribution.value && intentionDistribution.value.length > 0) {
      const intentionSheet = workbook.addWorksheet('意向等级分布')
      intentionSheet.columns = [
        { header: '意向等级', key: 'level', width: 15 },
        { header: '客户数量', key: 'count', width: 12 },
        { header: '占比(%)', key: 'percentage', width: 12 }
      ]
      
      intentionDistribution.value.forEach(item => {
        intentionSheet.addRow({
          level: item.level || '-',
          count: item.count || 0,
          percentage: item.percentage ? Number(item.percentage).toFixed(2) : '0.00'
        })
      })
      
      // 设置标题行样式
      intentionSheet.getRow(1).font = { bold: true }
      intentionSheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' }
      }
    }

    // 生成文件名（包含日期范围）
    const [startDate, endDate] = dateRange.value || []
    const fmt = (d) => d ? `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}` : ''
    const dateStr = startDate && endDate ? `_${fmt(startDate)}-${fmt(endDate)}` : ''
    const fileName = `数据看板报表${dateStr}_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`

    // 导出文件
    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    link.click()
    window.URL.revokeObjectURL(url)
    
    ElMessage.success('导出成功（包含图表）')
  } catch (error) {
    console.error('导出报表失败:', error)
    ElMessage.error('导出失败：' + (error.message || '未知错误'))
  } finally {
    exportLoading.value = false
  }
}

// 降级导出（不含图表，使用 xlsx 库）
const handleExportWithoutChart = async () => {
  try {
    // 检查xlsx库是否已加载
    if (typeof XLSX === 'undefined' || !XLSX.utils) {
      throw new Error('Excel导出库未加载，请先运行 npm install xlsx 安装依赖')
    }

    // 创建工作簿
    const wb = XLSX.utils.book_new()

    // 1. KPI数据工作表
    const kpiData = [
      ['指标', '数值'],
      ['新增客户', kpis.newCustomer || 0],
      ['跟进客户', kpis.follow || 0],
    ['合同创建', kpis.contractCreated || 0],
    ['成交数量/成交金额', `${kpis.dealCount || 0} / ${formatCurrency(kpis.dealAmount)}`]
    ]
    const kpiWs = XLSX.utils.aoa_to_sheet(kpiData)
    XLSX.utils.book_append_sheet(wb, kpiWs, '核心指标')

    // 2. 趋势图数据工作表（如果有数据）
    if (trend.value && trend.value.xAxis && trend.value.xAxis.length > 0) {
      const trendHeaders = ['日期/时间', ...(trend.value.series || []).map(s => s.name || '')]
      const trendData = [trendHeaders]
      
      trend.value.xAxis.forEach((xValue, index) => {
        const row = [xValue]
        trend.value.series.forEach(series => {
          row.push(series.data && series.data[index] ? series.data[index] : 0)
        })
        trendData.push(row)
      })
      
      const trendWs = XLSX.utils.aoa_to_sheet(trendData)
      trendWs['!cols'] = [{ wch: 15 }, ...trend.value.series.map(() => ({ wch: 12 }))]
      XLSX.utils.book_append_sheet(wb, trendWs, '趋势数据')
    }

    // 3. 业务员排名数据工作表
    if (ranking.value && ranking.value.length > 0) {
    const rankingHeaders = ['排名', '姓名', '工号', '新增客户', '跟进客户', '合同创建', '成交数量', '成交金额']
      const rankingData = [rankingHeaders]
      
      ranking.value.forEach((item, index) => {
        rankingData.push([
          index + 1,
          item.name || '-',
          item.employeeNo || '-',
          item.newCustomer || 0,
          item.follow || 0,
          item.contractCreated || 0,
        item.dealCount || 0,
        Number(item.dealAmount || 0)
        ])
      })
      
      const rankingWs = XLSX.utils.aoa_to_sheet(rankingData)
      rankingWs['!cols'] = [
      { wch: 8 }, { wch: 12 }, { wch: 15 }, { wch: 12 },
      { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 14 }
      ]
      XLSX.utils.book_append_sheet(wb, rankingWs, '业务员排名')
    }

    // 4. 意向等级分布数据工作表
    if (intentionDistribution.value && intentionDistribution.value.length > 0) {
      const intentionHeaders = ['意向等级', '客户数量', '占比(%)']
      const intentionData = [intentionHeaders]
      
      intentionDistribution.value.forEach(item => {
        intentionData.push([
          item.level || '-',
          item.count || 0,
          item.percentage ? Number(item.percentage).toFixed(2) : '0.00'
        ])
      })
      
      const intentionWs = XLSX.utils.aoa_to_sheet(intentionData)
      intentionWs['!cols'] = [{ wch: 15 }, { wch: 12 }, { wch: 12 }]
      XLSX.utils.book_append_sheet(wb, intentionWs, '意向等级分布')
    }

    // 生成文件名
    const [startDate, endDate] = dateRange.value || []
    const fmt = (d) => d ? `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}` : ''
    const dateStr = startDate && endDate ? `_${fmt(startDate)}-${fmt(endDate)}` : ''
    const fileName = `数据看板报表${dateStr}_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`

    XLSX.writeFile(wb, fileName)
    ElMessage.success('导出成功（不含图表）')
  } catch (error) {
    console.error('导出报表失败:', error)
    ElMessage.error('导出失败：' + (error.message || '未知错误'))
  }
}

onMounted(async () => {
  setDefaultRange()
  await loadSalesmanOptions()
  await loadDashboard()
})
</script>

<style scoped>
.manager-report {
  padding: 20px;
}
.toolbar-card {
  margin-bottom: 16px;
}
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.toolbar-title {
  font-size: 18px;
  font-weight: 600;
}
.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: -40px; /* 整体往左移动，使筛选条件更靠近左侧 */
}
.date-filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  background-color: #f5f7fa;
  border-radius: 6px;
}
.date-buttons-row {
  display: flex;
  gap: 8px;
}
.date-buttons-row .el-button {
  flex: 1;
  padding: 8px 12px;
  font-size: 13px;
  border-radius: 4px;
  transition: all 0.3s;
  min-width: 0;
}
.date-buttons-row .el-button:hover {
  transform: translateY(-1px);
}
.kpi-row {
  margin-bottom: 16px;
}
.kpi-card {
  text-align: left;
  border-radius: 10px;
}
.kpi-label {
  color: #666;
  margin-bottom: 8px;
}
.kpi-value {
  font-size: 30px;
  font-weight: 700;
  color: #409eff;
}
.chart-card,
.ranking-card,
.intention-card {
  margin-bottom: 16px;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
}
.ranking-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.ranking-item {
  display: flex;
  align-items: center;
  padding: 10px;
  border: 1px solid #f1f2f5;
  border-radius: 8px;
}
.rank-index {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #409eff;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  font-weight: 600;
}
.rank-info {
  flex: 1;
}
.rank-name {
  font-weight: 600;
}
.rank-meta {
  color: #999;
  font-size: 12px;
}
.rank-metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(60px, 80px));
  gap: 4px 8px;
  text-align: right;
  font-size: 13px;
}
.rank-metrics .deal {
  color: #f56c6c;
  font-weight: 600;
}
.intention-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
}
.intention-item {
  padding: 8px 10px;
  border: 1px solid #f1f2f5;
  border-radius: 8px;
}
.intention-label {
  font-weight: 600;
  margin-bottom: 6px;
}
.intention-count {
  color: #666;
  font-size: 12px;
  margin-top: 4px;
}
</style>

